import "../forms/ctl-select.js";
import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-color-muted: var(--ctl-color-muted);
    --ctl-color-border: var(--ctl-color-border);
    --ctl-color-surface: var(--ctl-color-surface);
    --ctl-color-surface-2: var(--ctl-color-surface-2);
    --ctl-color-brand: var(--ctl-color-brand);
    --ctl-focus-ring: var(--ctl-focus-ring);

    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .root{
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    background: var(--ctl-color-surface);
  }

  :host([variant="compact"]) .root{
    padding: var(--ctl-space-2) var(--ctl-space-2);
  }

  :host([disabled]) .root{
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .left, .right{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    min-width: 0;
  }

  .meta{
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
    white-space: nowrap;
  }

  .controls{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-1);
  }

  button{
    all: unset;
    cursor: pointer;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    height: var(--ctl-size-button-sm);
    min-width: var(--ctl-size-button-sm);
    padding: 0 var(--ctl-space-2);
    border-radius: var(--ctl-radius-md);
    border: var(--ctl-border-width-thin) solid color-mix(in srgb, var(--ctl-color-border) 85%, transparent);
    background: color-mix(in srgb, var(--ctl-color-border) 14%, transparent);
    color: var(--ctl-color-text);
    font-weight: var(--ctl-font-weight-extrabold);
    font-size: var(--ctl-font-size-md);
  }

  :host([variant="compact"]) button{
    height: var(--ctl-space-6);
    min-width: var(--ctl-space-6);
    border-radius: calc(var(--ctl-radius-10) + var(--ctl-space-0-5));
    padding: 0 calc(var(--ctl-space-2) + var(--ctl-space-0-5));
  }

  button:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 24%, transparent);
  }

  button:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  button[disabled]{
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }
  button[disabled]:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 14%, transparent);
  }

  button[data-role="page"][aria-current="page"]{
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, var(--ctl-color-surface));
    border-color: color-mix(in srgb, var(--ctl-color-brand) 22%, var(--ctl-color-border));
  }

  .gap{
    padding: 0 var(--ctl-space-1-5);
    color: var(--ctl-color-muted);
    user-select: none;
    font-weight: var(--ctl-font-weight-extrabold);
  }

  .jump{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
    white-space: nowrap;
  }

  input{
    width: calc(var(--ctl-space-7) + var(--ctl-space-5));
    height: var(--ctl-size-button-sm);
    border-radius: var(--ctl-radius-md);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    padding: 0 var(--ctl-space-2);
    font: inherit;
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-text);
    background: var(--ctl-color-surface);
  }

  :host([variant="compact"]) input{
    height: var(--ctl-space-6);
    border-radius: calc(var(--ctl-radius-10) + var(--ctl-space-0-5));
  }

  input:focus{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 35%, var(--ctl-color-border));
  }

  .pageSize{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    white-space: nowrap;
  }

  .pageSizeLabel{
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
  }

  ctl-select{
    width: calc(var(--ctl-space-7) * 2.29);
  }

  :host([variant="compact"]) .meta,
  :host([variant="compact"]) .jump,
  :host([variant="compact"]) .pageSizeLabel{
    font-size: var(--ctl-font-size-sm);
  }

  @media (max-width: 560px){
    .root{ flex-wrap: wrap; }
    .right{ width: 100%; justify-content: space-between; }
    ctl-select{ width: calc(var(--ctl-space-7) * 2.5); }
  }
</style>

<div class="root" part="root">
  <div class="left" part="left">
    <div class="meta" id="meta" part="meta"></div>
  </div>

  <div class="right" part="right">
    <div class="controls" id="controls" part="controls"></div>

    <div class="jump" part="jump">
      <span>Go to</span>
      <input id="jump" type="number" min="1" inputmode="numeric" aria-label="Go to page" />
      <span id="jumpSuffix" class="meta"></span>
    </div>

    <div class="pageSize" part="pageSize">
      <span class="pageSizeLabel">Rows</span>
      <ctl-select id="pageSize"></ctl-select>
    </div>
  </div>
</div>
`;

function clampInt(v, d){
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : d;
}
function clampMin1(v, d){
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(1, Math.floor(n)) : d;
}
function uniqNums(arr){
  const set = new Set((Array.isArray(arr) ? arr : []).map((x) => clampMin1(x, 0)).filter((n) => n > 0));
  return Array.from(set).sort((a,b) => a-b);
}
function formatRange(page, pageSize, total){
  if (!total) return "0 results";
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return `${start}–${end} of ${total}`;
}

function pagesToShow(page, totalPages){
  // Show: first, last, current +/- 1, and fill to ~7 buttons with gaps.
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const set = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
  const clamped = Array.from(set).filter((p) => p >= 1 && p <= totalPages).sort((a,b) => a-b);

  // Insert gaps
  const out = [];
  let prev = null;
  for (const p of clamped) {
    if (prev != null && p - prev > 1) out.push("gap");
    out.push(p);
    prev = p;
  }
  return out;
}

export class CtlPagination extends HTMLElement {
  static get observedAttributes(){
    return ["page","page-size","total-items","page-size-options","variant","disabled"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._meta = this.shadowRoot.getElementById("meta");
    this._controls = this.shadowRoot.getElementById("controls");
    this._jump = this.shadowRoot.getElementById("jump");
    this._jumpSuffix = this.shadowRoot.getElementById("jumpSuffix");
    this._pageSize = this.shadowRoot.getElementById("pageSize");

    this._onClick = this._onClick.bind(this);
    this._onJump = this._onJump.bind(this);
    this._onPageSize = this._onPageSize.bind(this);
    this._onJumpKeydown = this._onJumpKeydown.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("variant")) this.setAttribute("variant","full");
    if (!this.hasAttribute("page")) this.setAttribute("page","1");
    if (!this.hasAttribute("page-size")) this.setAttribute("page-size","25");
    if (!this.hasAttribute("total-items")) this.setAttribute("total-items","0");

    if (!this.hasAttribute("page-size-options")) {
      this.setAttribute("page-size-options", JSON.stringify([10,25,50,100]));
    }

    this.shadowRoot.addEventListener("click", this._onClick);
    this._jump.addEventListener("change", this._onJump);
    this._jump.addEventListener("keydown", this._onJumpKeydown);

    this._pageSize.addEventListener("ctl-change", this._onPageSize);

    // hydrate ctl-select options
    this._syncPageSizeOptions();

    this._render();
  }

  disconnectedCallback(){
    this.shadowRoot.removeEventListener("click", this._onClick);
    this._jump.removeEventListener("change", this._onJump);
    this._jump.removeEventListener("keydown", this._onJumpKeydown);
    this._pageSize.removeEventListener("ctl-change", this._onPageSize);
  }

  attributeChangedCallback(name){
    if (name === "variant") {
      const v = (this.getAttribute("variant") === "compact") ? "compact" : "full";
      if (this.getAttribute("variant") !== v) this.setAttribute("variant", v);
    }
    if (name === "page" || name === "page-size" || name === "total-items" || name === "disabled") {
      this._render();
    }
    if (name === "page-size-options") {
      this._syncPageSizeOptions();
      this._render();
    }
  }

  // ---- props ----
  get page(){ return clampMin1(this.getAttribute("page"), 1); }
  set page(v){ this.setAttribute("page", String(clampMin1(v, 1))); }

  get pageSize(){ return clampMin1(this.getAttribute("page-size"), 25); }
  set pageSize(v){ this.setAttribute("page-size", String(clampMin1(v, 25))); }

  get totalItems(){ return clampInt(this.getAttribute("total-items"), 0); }
  set totalItems(v){ this.setAttribute("total-items", String(clampInt(v, 0))); }

  get pageSizeOptions(){
    const raw = safeJsonParse(this.getAttribute("page-size-options"), [10,25,50,100]);
    return uniqNums(raw.length ? raw : [10,25,50,100]);
  }
  set pageSizeOptions(v){
    this.setAttribute("page-size-options", JSON.stringify(uniqNums(v)));
  }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  get totalPages(){
    const total = this.totalItems;
    const size = this.pageSize;
    if (!total || !size) return 0;
    return Math.max(1, Math.ceil(total / size));
  }

  _syncPageSizeOptions(){
    const opts = this.pageSizeOptions;
    const current = this.pageSize;

    // ctl-select API: value + options (assuming your ctl-select supports .options)
    // We'll set both attrs to be safe with your current component patterns.
    const options = opts.map((n) => ({ value: String(n), label: String(n) }));

    // prefer property if available
    try { this._pageSize.options = options; } catch {}
    this._pageSize.setAttribute("options", JSON.stringify(options));

    // ensure current size is in options
    if (!opts.includes(current)) {
      opts.push(current);
      opts.sort((a,b) => a-b);
    }

    this._pageSize.value = String(current);
    this._pageSize.setAttribute("value", String(current));
  }

  _render(){
    const totalPages = this.totalPages;
    const totalItems = this.totalItems;
    const pageSize = this.pageSize;

    // clamp current page
    let page = this.page;
    if (totalPages > 0) page = Math.min(page, totalPages);
    else page = 1;
    if (page !== this.page) this.setAttribute("page", String(page));

    // meta
    this._meta.textContent = formatRange(page, pageSize, totalItems);
    this._jumpSuffix.textContent = totalPages ? `of ${totalPages}` : "";

    // jump input
    this._jump.value = totalPages ? String(page) : "";
    this._jump.disabled = this.disabled || totalPages === 0;
    this._jump.max = totalPages ? String(totalPages) : "1";

    // page size select
    const canChangeSize = !this.disabled;
    this._pageSize.toggleAttribute("disabled", !canChangeSize);
    // keep value in sync
    this._pageSize.value = String(pageSize);
    this._pageSize.setAttribute("value", String(pageSize));

    // controls
    const disabledAll = this.disabled || totalPages === 0;
    const show = pagesToShow(page, totalPages || 1);

    const btn = (label, role, disabled, extra = "") => `
      <button type="button" data-role="${role}" ${disabled ? "disabled" : ""} ${extra}>
        ${label}
      </button>
    `;

    const pageBtn = (p) => `
      <button
        type="button"
        data-role="page"
        data-page="${p}"
        ${disabledAll ? "disabled" : ""}
        ${p === page ? `aria-current="page"` : ""}
        aria-label="Page ${p}"
      >${p}</button>
    `;

    const parts = [];

    if (this.getAttribute("variant") !== "compact") {
      parts.push(btn("«", "first", disabledAll || page <= 1, `aria-label="First page"`));
      parts.push(btn("‹", "prev", disabledAll || page <= 1, `aria-label="Previous page"`));
    } else {
      parts.push(btn("‹", "prev", disabledAll || page <= 1, `aria-label="Previous page"`));
    }

    if (totalPages > 0) {
      for (const x of show) {
        if (x === "gap") parts.push(`<span class="gap" aria-hidden="true">…</span>`);
        else parts.push(pageBtn(x));
      }
    }

    if (this.getAttribute("variant") !== "compact") {
      parts.push(btn("›", "next", disabledAll || page >= totalPages, `aria-label="Next page"`));
      parts.push(btn("»", "last", disabledAll || page >= totalPages, `aria-label="Last page"`));
    } else {
      parts.push(btn("›", "next", disabledAll || page >= totalPages, `aria-label="Next page"`));
    }

    this._controls.innerHTML = parts.join("");
  }

  _emitPage(){
    this.dispatchEvent(new CustomEvent("ctl-page", {
      bubbles: true,
      composed: true,
      detail: {
        page: this.page,
        pageSize: this.pageSize,
        totalItems: this.totalItems,
        totalPages: this.totalPages
      }
    }));
  }

  _emitPageSize(){
    this.dispatchEvent(new CustomEvent("ctl-page-size", {
      bubbles: true,
      composed: true,
      detail: {
        pageSize: this.pageSize,
        page: this.page,
        totalItems: this.totalItems,
        totalPages: this.totalPages
      }
    }));
  }

  _onClick(e){
    const b = e.target?.closest?.("button[data-role]");
    if (!b) return;
    if (this.disabled) return;

    const role = b.getAttribute("data-role");
    const totalPages = this.totalPages;
    if (!totalPages) return;

    let page = this.page;

    if (role === "first") page = 1;
    if (role === "prev") page = Math.max(1, page - 1);
    if (role === "next") page = Math.min(totalPages, page + 1);
    if (role === "last") page = totalPages;
    if (role === "page") page = Number(b.getAttribute("data-page")) || page;

    if (page === this.page) return;

    this.page = page;
    this._render();
    this._emitPage();
  }

  _onJump(){
    if (this.disabled) return;
    const totalPages = this.totalPages;
    if (!totalPages) return;

    const n = Number(this._jump.value);
    if (!Number.isFinite(n)) return;

    const next = Math.max(1, Math.min(totalPages, Math.floor(n)));
    if (next === this.page) return;

    this.page = next;
    this._render();
    this._emitPage();
  }

  _onPageSize(e){
    if (this.disabled) return;

    const nextSize = Number(e?.detail?.value);
    if (!Number.isFinite(nextSize) || nextSize <= 0) return;

    // Keep current first item visible: compute index, then recompute page
    const currentFirstIndex = (this.page - 1) * this.pageSize; // 0-based
    const nextPage = Math.floor(currentFirstIndex / nextSize) + 1;

    this.pageSize = nextSize;
    this.page = nextPage;

    this._syncPageSizeOptions();
    this._render();
    this._emitPageSize();
    this._emitPage();
  }

  _onJumpKeydown(e) {
    if (e.key === "Enter") this._onJump();
  }
}

customElements.define("ctl-pagination", CtlPagination);
